import React, { useEffect, useState } from "react";
import {
  Modal,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ActivityIndicator,
} from "react-native";
import { AlertTriangle, X } from "lucide-react-native";
import StyledText from "../../common/ui/styled-text";

interface IntegrationDisconnectConfirmModalProps {
  visible: boolean;
  providerName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const IntegrationDisconnectConfirmModal = ({
  visible,
  providerName,
  onConfirm,
  onCancel,
  isLoading = false,
}: IntegrationDisconnectConfirmModalProps) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!visible) {
      setIsChecked(false);
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onCancel}
    >
      <TouchableWithoutFeedback onPress={onCancel}>
        <View className="flex-1 bg-black/60 justify-center items-center px-4">
          <TouchableWithoutFeedback>
            <View className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
              <View className="flex-row items-center mb-4">
                <View className="w-10 h-10 bg-orange-100 rounded-full items-center justify-center mr-3">
                  <AlertTriangle size={20} color="#EA580C" />
                </View>
                <View className="flex-1">
                  <StyledText className="text-xl font-bold text-orange-900">
                    Disconnect {providerName}
                  </StyledText>
                </View>
                <TouchableOpacity
                  onPress={onCancel}
                  className="w-8 h-8 items-center justify-center"
                >
                  <X size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>

              <View className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <StyledText className="text-orange-800 font-semibold text-base mb-2">
                  ⚠️ This will stop your AI agents
                </StyledText>
                <StyledText className="text-orange-700 text-sm leading-5">
                  Disconnecting {providerName} will immediately stop all your AI
                  agents from processing orders. You would need to:
                </StyledText>
                <View className="mt-3 space-y-1">
                  <StyledText className="text-orange-700 text-sm">
                    • Reconnect the integration to resume order processing
                  </StyledText>
                  <StyledText className="text-orange-700 text-sm">
                    • Reconfigure your connected locations
                  </StyledText>
                  <StyledText className="text-orange-700 text-sm">
                    • Set up the integration again
                  </StyledText>
                </View>
              </View>

              <TouchableOpacity
                onPress={() => setIsChecked((prev) => !prev)}
                className="flex-row items-start mb-6"
              >
                <View
                  className={`w-5 h-5 border-2 rounded mr-3 items-center justify-center mt-0.5 ${
                    isChecked
                      ? "bg-orange-600 border-orange-600"
                      : "border-orange-400"
                  }`}
                >
                  {isChecked && (
                    <StyledText className="text-white text-xs font-bold">
                      ✓
                    </StyledText>
                  )}
                </View>
                <StyledText className="text-sm text-gray-800 flex-1 leading-5">
                  I understand that disconnecting will stop my AI agents and I
                  would need to reconnect to resume service.
                </StyledText>
              </TouchableOpacity>

              <View className="flex-row space-x-3">
                <TouchableOpacity
                  onPress={onCancel}
                  className="flex-1 px-4 py-3 bg-gray-100 rounded-lg items-center justify-center"
                >
                  <StyledText className="text-gray-700 font-semibold">
                    Cancel
                  </StyledText>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={onConfirm}
                  className={`flex-1 px-4 py-3 rounded-lg items-center ${
                    !isChecked ? "bg-gray-300" : "bg-orange-600"
                  }`}
                  disabled={isLoading || !isChecked}
                >
                  {isLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <StyledText className="text-white font-semibold text-base">
                      Disconnect
                    </StyledText>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default IntegrationDisconnectConfirmModal;
